import { forwardRef, useContext, useState } from 'react';
import styles from './Button.module.scss.js';
import { ButtonContent } from './ButtonContent.js';
import { UNSAFE_ButtonContext } from './ButtonContext.js';
import { jsx } from 'react/jsx-runtime';
import { use_UNSAFEUserAgentInfo } from '../util/use_UNSAFEUserAgent.js';
import classNames from 'clsx';

function getVariant(callerVariant, inverse) {
  if (inverse) {
    if (callerVariant === 'primary') {
      return 'inverse-primary';
    }
    if (callerVariant === 'default') {
      return 'inverse-default';
    }
    if (callerVariant === 'ui-control') {
      return 'inverse-ui-control';
    }
  }
  return callerVariant;
}
const Button = /*#__PURE__*/forwardRef(
/**
 * A button with optional icon.
 */
function Button(props, ref) {
  const {
    actionIndicator,
    children,
    className: callerClassName,
    disabled,
    icon,
    iconOnly,
    label,
    loadingState = 'initial',
    size: callerSize = 'default',
    tag,
    type = 'button',
    variant: callerVariant = 'default',
    wrap = true,
    UNSAFE_active,
    UNSAFE_hover,
    UNSAFE_focus,
    onMouseDown,
    ...rest
  } = props;
  const ctx = useContext(UNSAFE_ButtonContext);
  const size = ctx.size ?? callerSize;
  const variant = getVariant(callerVariant, ctx.inverse ?? false);
  const isDisabled = disabled || loadingState === 'loading';
  const needsTitle = iconOnly || !childIsString(children) || loadingState === 'loading';
  const title = needsTitle ? childrenToString(children, label) : undefined;

  // Firefox does not apply the :active pseudoclass when preventDefault is used in onMouseDown.
  // This is apparently undefined behavior according to the spec, but other browsers do it, so
  // we can force the behavior in Firefox.
  // https://bugzilla.mozilla.org/show_bug.cgi?id=771241
  const ua = use_UNSAFEUserAgentInfo();
  const shouldUseForceActive = ua.browser === 'firefox';
  const [forceActive, setForceActive] = useState(false);
  function handleMouseDown(e) {
    if (shouldUseForceActive) {
      setForceActive(true);
      document.addEventListener('mouseup', handleMouseUp);
    }
    onMouseDown?.(e);
  }
  function handleMouseUp() {
    setForceActive(false);
    document.removeEventListener('mouseup', handleMouseUp);
  }
  const className = classNames('dsx-component', styles['button'], styles[`size-${size}`], styles[wrap ? 'wrap' : 'nowrap'], styles[variant], (UNSAFE_active || forceActive) && styles['active'], UNSAFE_hover && styles['hover'], UNSAFE_focus && styles['focus'], loadingState === 'loading' && styles['loading'], callerClassName);
  return /*#__PURE__*/jsx("button", {
    ...rest,
    className: className,
    ref: ref,
    type: type,
    "data-dsx--button": tag,
    "aria-label": title,
    disabled: isDisabled,
    onMouseDown: handleMouseDown,
    children: /*#__PURE__*/jsx(ButtonContent, {
      ...props
    })
  });
});
function childIsString(arg) {
  return typeof arg === 'string';
}
function childrenToString(arg, fallback) {
  if (childIsString(arg)) {
    return arg;
  }
  return fallback;
}

export { Button };
